import type { FamilyMemberCondition } from '@models/family-history';

interface RawCondition {
  name?: string | null;
  /** Older discern contract. */
  age_diagnosed?: string | null;
  /** What discern-modelling's map_family emits (DISC-278). */
  ageDiagnosed?: string | null;
}

interface FamilyHistoryOcrResult {
  conditions: FamilyMemberCondition[];
  [key: string]: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isRawCondition(value: unknown): value is RawCondition {
  return isRecord(value);
}

/**
 * Idempotent condition row type guard: detects when mergeOcrFromConfig has
 * already run once and produced FamilyMemberCondition objects.
 *
 * Rows carrying ageDiagnosed or diagnosedYear are already in form shape.
 * Checking for the key's presence rather than its value ensures a raw
 * condition isn't taken for processed just because it states no age.
 *
 * Discern's camelCase output (DISC-278) satisfies this too, which is harmless:
 * such a row is already the shape the form wants. Rows that don't match fall
 * through to the conversion below, which reads either spelling.
 */
function isConditionRow(value: unknown): value is FamilyMemberCondition {
  return (
    isRecord(value) &&
    typeof value.name === 'string' &&
    ('ageDiagnosed' in value || 'diagnosedYear' in value)
  );
}

/**
 * Parse raw conditions array from Discern OCR into the form's FamilyMemberCondition[] shape.
 *
 * Handles:
 * - A real array of condition objects
 * - A JSON string that parses to an array
 * - An already-processed array (idempotent for double-merge safety)
 * - Missing/null conditions (returns empty array)
 *
 * Field naming: the form uses camelCase (ageDiagnosed). Discern has emitted
 * both spellings — snake_case age_diagnosed historically, camelCase since
 * DISC-278 — so both are read here rather than coupling to one.
 */
function parseConditions(value: unknown): FamilyMemberCondition[] {
  if (value === null || value === undefined) return [];

  let raw: unknown = value;

  // Try to parse if it's a JSON string
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [];
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }

  // Normalize to array
  if (raw === null || raw === undefined) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  if (!list.length) return [];

  // If already processed by a prior merge pass, return as-is
  const alreadyRows = list.filter(isConditionRow);
  if (alreadyRows.length === list.length && list.length > 0) {
    return alreadyRows;
  }

  // Convert raw Discern conditions to form shape
  const conditions = list
    .filter(isRawCondition)
    .map((condition): FamilyMemberCondition => {
      const name = condition.name ? String(condition.name).trim() : undefined;
      // Accept either spelling: discern emits ageDiagnosed, the older
      // contract used age_diagnosed. Reading only one silently drops the age
      // on any row that used the other.
      const rawAge = condition.age_diagnosed ?? condition.ageDiagnosed;
      const ageDiagnosed = rawAge ? String(rawAge).trim() : undefined;

      return {
        ...(name && { name }),
        ...(ageDiagnosed && { ageDiagnosed }),
      };
    })
    .filter((c) => c.name); // Drop entries with no name

  return conditions;
}

function trimmed(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Rebuild the single condition row from discern's flat labels.
 *
 * The nested `conditions` array is unreachable in practice: discern sends it as
 * a real array, and buildFlatAndMap keeps only string `text`, so it never
 * reaches `flat`. `description` (the condition name) and `ageDiagnosed` do
 * arrive as strings, and unlike a serialized array they carry their own bbox
 * evidence, so the curator can still click through to the source span.
 *
 * One row only — discern's FamilyHistory model holds a single condition per
 * family member, so a second row could never be populated from this path.
 */
function conditionsFromFlatFields(
  flat: Record<string, unknown>
): FamilyMemberCondition[] {
  const name = trimmed(flat.description);
  if (!name) return [];

  const ageDiagnosed = trimmed(flat.ageDiagnosed);

  return [{ name, ...(ageDiagnosed && { ageDiagnosed }) }];
}

/**
 * Assemble Family History OCR data into the form's expected shape.
 *
 * Transforms discern's flat OCR output into the form's structure:
 * - conditions[] is parsed from a potential JSON string into FamilyMemberCondition[]
 * - Field naming is normalized (snake_case → camelCase)
 * - falls back to the flat `description` + `ageDiagnosed` labels, which is the
 *   only route that actually survives BBOX (see conditionsFromFlatFields)
 * - All other fields pass through as-is (relationshipCode, maternalPaternal, etc.)
 *
 * Idempotent: safe to call twice on the same record (mergeOcrFromConfig runs it twice).
 */
export function assembleFamilyHistoryOcrRecord(
  flat: Record<string, unknown>
): FamilyHistoryOcrResult {
  const parsed = parseConditions(flat.conditions);
  const conditions = parsed.length ? parsed : conditionsFromFlatFields(flat);

  return {
    ...flat,
    conditions,
  };
}
